import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '@/contexts/AuthContext';
import { storeProfileAPI, ownerSubUsersAPI } from '@/lib/api';
import { computeEffectiveStatus } from '@/lib/tierConfig';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import SubscriptionWarningBanner from '@/components/SubscriptionWarningBanner';
import {
  Save, RefreshCw, CheckCircle2, Crown, Zap, Rocket,
  Package, TrendingUp, Calendar, Lock,
  Eye, EyeOff, ChevronRight, ChevronDown, AlertCircle, Sliders,
  PiggyBank, Printer,
  Sun, Moon, Monitor, Users, Pencil, X as XIcon, Check,
  MessageCircle, Phone, Mail, HelpCircle, ShieldCheck, ShieldOff,
  Truck,
} from 'lucide-react';

// ── Membership tier config ─────────────────────────────────────────────────
const TIERS = {
  starter: {
    label: 'Starter', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: Zap, price: '₹xxx', period: '/month',
    features: [
      'Your Savings dashboard',
      'Stock management',
      'Appendix & reports',
      'Smart action threshold customisation',
    ],
  },
  growth: {
    label: 'Growth', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    icon: Rocket, price: '₹xxx', period: '/month', popular: true,
    features: [
      'Everything in Starter',
      'Smart Actions page',
      'Smart Action buttons in Stock',
      'AI-powered reorder & margin recommendations',
    ],
  },
  enterprise: {
    label: 'Enterprise', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: Crown, price: '₹xxx', period: '/month',
    features: [
      'Everything in Growth',
      'Customer Insights page',
      'Full customer analytics suite',
    ],
  },
};

const TIER_ORDER = ['starter', 'growth', 'enterprise'];

const HELP_CONTACTS = [
  { name: 'Rohan Kakad',    phone: '8888855212', email: 'rohankakad712@gmail.com' },
  { name: 'Shlok Deore',   phone: '8788364128', email: 'shlokdeore@gmail.com'    },
  { name: 'Rupesh Shewale', phone: '8237598340', email: 'rupeshshewale02@gmail.com' },
];

const SUPPORT_WHATSAPP = 'https://wa.me/918888855212';
const SUPPORT_PHONE    = 'tel:+918888855212';

const openRenewChat = (tier, action = 'renew') => {
  const msg = action === 'upgrade'
    ? `Hi, I'd like to upgrade my KiranaFlow subscription to the ${tier} plan.`
    : `Hi, I'd like to renew my KiranaFlow ${tier} plan subscription.`;
  window.open(`${SUPPORT_WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');
};

const statusColor = {
  active:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  suspended: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  expired:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
};

const fmtCurrency = (val) => {
  if (val === null || val === undefined) return '—';
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}k`;
  return `₹${Math.round(val)}`;
};

// ── Sub-components ─────────────────────────────────────────────────────────
const Section = ({ title, desc, icon: Icon, children, badge }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        {title}
        {badge && (
          <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {badge}
          </span>
        )}
      </CardTitle>
      {desc && <CardDescription>{desc}</CardDescription>}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const Field = ({ label, id, children, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
    {children || <Input id={id} {...props} />}
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
    <div className={`p-2 rounded-md ${accent || 'bg-primary/10 text-primary'}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-lg font-bold leading-none truncate">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

const HelpCard = () => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-primary" /> Help & Support
      </CardTitle>
      <CardDescription>Reach out to us for any issues or questions</CardDescription>
    </CardHeader>
    <CardContent className="space-y-3">
      {HELP_CONTACTS.map(({ name, phone, email }) => (
        <div key={phone} className="rounded-lg border border-border p-3 space-y-2">
          <p className="text-sm font-semibold">{name}</p>
          <div className="flex flex-col gap-1.5">
            <a
              href={`https://wa.me/91${phone}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
              <span>{phone}</span>
            </a>
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Mail className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              <span className="truncate">{email}</span>
            </a>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

// ── Main Page ──────────────────────────────────────────────────────────────
export default function Settings() {
  const qc = useQueryClient();
  const { user, storeProfile, refreshStoreProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mfaEnrolled, setMfaEnrolled] = useState(null); // null=loading, true/false
  const [stats, setStats] = useState(null);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [savedReceipt, setSavedReceipt] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [savedThresholds, setSavedThresholds] = useState(false);
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [editingSubUser, setEditingSubUser] = useState(null); // { id, username, password }
  const [subUserSaving, setSubUserSaving] = useState(false);

  const emptyForm = {
    shop_name: '', owner_name: '', tagline: '',
    address: '', city: '', state: '', pincode: '',
    phone: '', email: '', gstin: '', food_license_number: '',
    bank_name: '', bank_account: '', bank_ifsc: '', upi_id: '',
    receipt_footer: 'Thank you for shopping with us!',
    receipt_format: '80mm',
    return_policy: 'Goods once sold will not be taken back',
    lead_time_days: 3,
    buffer_days: 2,
    expiry_alert_days: 15,
    dead_stock_days: 30,
    critical_stock_days: 2,
    warning_stock_days: 7,
    membership_tier: 'starter', membership_status: 'active',
    membership_valid_until: null, membership_started_at: null,
    supplier_visibility: { sales_velocity: true, dead_stock: true, expiry_tracking: true },
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const enrolled = (data?.totp || []).some(f => f.status === 'verified');
      setMfaEnrolled(enrolled);
    }).catch(() => setMfaEnrolled(false));
  }, []);

  useEffect(() => {
    if (user?.role !== 'cashier') {
      storeProfileAPI.getStats().then(r => setStats(r.data)).catch(() => {
        toast.error('Could not load business stats. Check your connection.');
      });

      if (storeProfile) {
        setForm(f => ({ ...f, ...storeProfile }));
      }
    }

    if (user?.role === 'owner') {
      ownerSubUsersAPI.list().then(r => setSubUsers(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const handleSubUserSave = async () => {
    if (!editingSubUser) return;
    const { id, username, password } = editingSubUser;
    if (password && password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const updates = {};
    if (username !== undefined) updates.username = username;
    if (password) updates.password = password;
    if (!Object.keys(updates).length) { setEditingSubUser(null); return; }
    setSubUserSaving(true);
    try {
      await ownerSubUsersAPI.update(id, updates);
      setSubUsers(prev => prev.map(u => u.id === id ? { ...u, username: username ?? u.username } : u));
      setEditingSubUser(null);
      toast.success('Credentials updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials');
    } finally { setSubUserSaving(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm(f => ({ ...f, [k]: Number(e.target.value) }));

  const handleSaveReceipt = async () => {
    setSavingReceipt(true);
    try {
      await storeProfileAPI.update(form);
      await refreshStoreProfile();
      setSavedReceipt(true);
      toast.success('Receipt settings saved!');
      setTimeout(() => setSavedReceipt(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingReceipt(false); }
  };

  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
      await storeProfileAPI.update(form);
      await refreshStoreProfile();
      qc.invalidateQueries({ queryKey: ['stock-dead'] });
      qc.invalidateQueries({ queryKey: ['smart-actions-open'] });
      qc.invalidateQueries({ queryKey: ['smart-actions-addressed'] });
      setSavedThresholds(true);
      toast.success('Thresholds saved!');
      setTimeout(() => setSavedThresholds(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingThresholds(false); }
  };

  const [savingDistributor, setSavingDistributor] = useState(false);
  const [savedDistributor,  setSavedDistributor]  = useState(false);

  const toggleVisibility = (key) =>
    setForm(f => ({
      ...f,
      supplier_visibility: { ...f.supplier_visibility, [key]: !f.supplier_visibility?.[key] },
    }));

  const handleSaveDistributorAccess = async () => {
    setSavingDistributor(true);
    try {
      await storeProfileAPI.update(form);
      await refreshStoreProfile();
      setSavedDistributor(true);
      toast.success('Distributor access settings saved!');
      setTimeout(() => setSavedDistributor(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingDistributor(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) return toast.error('Passwords do not match');
    if (pwForm.new_password.length < 6) return toast.error('Minimum 6 characters required');
    setPwLoading(true);
    try {
      await storeProfileAPI.changePassword({ old_password: pwForm.old_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully!');
      setPwForm({ old_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  const normalisedTier = TIERS[form.membership_tier] ? form.membership_tier : 'starter';
  const tier = TIERS[normalisedTier];
  const TierIcon = tier.icon;
  const days = daysLeft(form.membership_valid_until);
  const expirySoon = days !== null && days <= 7;

  // Cashiers: appearance only — no store data, no billing config, no membership
  if (user?.role === 'cashier') {
    return (
      <div className="p-6 space-y-6 max-w-sm">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sliders className="h-6 w-6 text-primary" /> Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Appearance preferences</p>
        </div>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Monitor className="h-4 w-4 text-primary" /> Appearance
            </CardTitle>
            <CardDescription>Choose your preferred colour scheme</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {[
                { value: 'light',  label: 'Light',  Icon: Sun     },
                { value: 'system', label: 'System', Icon: Monitor },
                { value: 'dark',   label: 'Dark',   Icon: Moon    },
              ].map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTheme(value)}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 text-xs font-medium transition-colors
                    ${theme === value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        <HelpCard />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sliders className="h-6 w-6 text-primary" /> Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Store profile, Smart Action thresholds, password, and your plan details
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${tier.color}`}>
            {tier.label}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[computeEffectiveStatus(form.membership_status, form.membership_valid_until)] || statusColor.active}`}>
            {computeEffectiveStatus(form.membership_status, form.membership_valid_until)}
          </span>
        </div>
      </div>

      {/* Membership expiry warning */}
      <SubscriptionWarningBanner validUntil={form.membership_valid_until} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN — thresholds + password */}
        <div className="lg:col-span-2 space-y-6">
          <div className="space-y-6">

            {/* Receipt Customization — all fields in the order they appear on the printed receipt */}
            <Section
              title="Receipt Customization"
              desc="Fields printed on every bill, in the order they appear"
              icon={Printer}
            >
              <Field label="Store Name *" id="shop_name" value={form.shop_name} onChange={set('shop_name')} placeholder="Jai Shree Ram Super Shop" />
              <Field label="Address" id="address" value={form.address} onChange={set('address')} placeholder="Shop No. 5, Main Road, Pune 411001" />
              <Field label="Phone" id="phone" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
              <Field label="GSTIN" id="gstin" value={form.gstin} onChange={set('gstin')} placeholder="27AABCX1234Z1ZV" />
              <Field label="FSSAI License Number" id="food_license_number" value={form.food_license_number} onChange={set('food_license_number')} placeholder="e.g. 10012345678901" />
              <div className="space-y-1.5">
                <Field label="UPI ID (for payment QR on receipt)" id="upi_id" value={form.upi_id} onChange={set('upi_id')} placeholder="e.g. shopname@upi or 9876543210@paytm" />
                <p className="text-xs text-muted-foreground -mt-0.5">When set, a scan-to-pay QR with the exact bill amount is printed on every receipt.</p>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="receipt_footer" className="text-sm font-medium">Footer Message</label>
                <Textarea
                  id="receipt_footer"
                  rows={2}
                  value={form.receipt_footer}
                  onChange={set('receipt_footer')}
                  placeholder="Thank you for shopping with us!"
                  className="resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="return_policy" className="text-sm font-medium">Return Policy</label>
                <Textarea
                  id="return_policy"
                  rows={2}
                  value={form.return_policy}
                  onChange={set('return_policy')}
                  placeholder="Goods once sold will not be taken back"
                  className="resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Paper Format</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={form.receipt_format === '80mm' ? 'default' : 'outline'}
                    className="gap-2"
                    onClick={() => setForm(f => ({ ...f, receipt_format: '80mm' }))}
                  >
                    <Printer className="h-4 w-4" /> 80mm Thermal
                  </Button>
                  <Button
                    type="button"
                    variant={form.receipt_format === '58mm' ? 'default' : 'outline'}
                    className="gap-2"
                    onClick={() => setForm(f => ({ ...f, receipt_format: '58mm' }))}
                  >
                    <Printer className="h-4 w-4" /> 58mm Thermal
                  </Button>
                  <Button
                    type="button"
                    variant={form.receipt_format === 'a4' ? 'default' : 'outline'}
                    className="gap-2"
                    onClick={() => setForm(f => ({ ...f, receipt_format: 'a4' }))}
                  >
                    <Printer className="h-4 w-4" /> A4 Paper
                  </Button>
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button type="button" disabled={savingReceipt} onClick={handleSaveReceipt} className="gap-2 min-w-[140px]">
                  {savingReceipt ? <><RefreshCw className="h-4 w-4 animate-spin" /> Saving…</>
                    : savedReceipt ? <><CheckCircle2 className="h-4 w-4" /> Saved!</>
                    : <><Save className="h-4 w-4" /> Save Receipt</>}
                </Button>
              </div>
            </Section>

            {/* Smart Action Thresholds — REQUIRED */}
            <Section
              title="Smart Action Thresholds"
              desc="Customise when alerts fire and how reorder quantities are calculated for your store"
              icon={Sliders}
              badge="Required"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Field label="Supplier Lead Time (days)" id="lead_time_days" type="number" min="1" max="14" step="1"
                    value={form.lead_time_days} onChange={setNum('lead_time_days')} />
                  <p className="text-xs text-muted-foreground mt-1">How many days it takes your supplier to deliver after you order</p>
                </div>
                <div>
                  <Field label="Buffer Stock (days)" id="buffer_days" type="number" min="0" max="10" step="1"
                    value={form.buffer_days} onChange={setNum('buffer_days')} />
                  <p className="text-xs text-muted-foreground mt-1">Extra days of safety stock to keep on hand beyond lead time</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Field label="Dead Stock Window (days without sales)" id="dead_stock_days" type="number" min="7" max="180"
                    value={form.dead_stock_days} onChange={setNum('dead_stock_days')} />
                  <p className="text-xs text-muted-foreground mt-1">Products with no sale in this many days are flagged as dead stock</p>
                </div>
                <div>
                  <Field label="Expiry Alert (days before expiry)" id="expiry_alert_days" type="number" min="1" max="90"
                    value={form.expiry_alert_days} onChange={setNum('expiry_alert_days')} />
                  <p className="text-xs text-muted-foreground mt-1">Get an alert this many days before a batch's expiry date</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Field label="Critical Stock (days of supply left)" id="critical_stock_days" type="number" min="1" max="14"
                    value={form.critical_stock_days} onChange={setNum('critical_stock_days')} />
                  <p className="text-xs text-muted-foreground mt-1">Stock this low triggers a high-priority reorder alert</p>
                </div>
                <div>
                  <Field label="Warning Stock (days of supply left)" id="warning_stock_days" type="number" min="2" max="30"
                    value={form.warning_stock_days} onChange={setNum('warning_stock_days')} />
                  <p className="text-xs text-muted-foreground mt-1">Stock this low triggers a standard reorder alert</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Changes apply immediately — your dashboard and Smart Actions will reflect new thresholds on next page load.
              </p>
              <div className="flex justify-end pt-1">
                <Button type="button" disabled={savingThresholds} onClick={handleSaveThresholds} className="gap-2 min-w-[160px]">
                  {savingThresholds ? <><RefreshCw className="h-4 w-4 animate-spin" /> Saving…</>
                    : savedThresholds ? <><CheckCircle2 className="h-4 w-4" /> Saved!</>
                    : <><Save className="h-4 w-4" /> Save Thresholds</>}
                </Button>
              </div>
            </Section>

          </div>

          {/* Security — 2FA status for all non-cashier roles */}
          {user?.role !== undefined && (
            <Section
              title="Security"
              desc="Two-factor authentication (TOTP) protects your account"
              icon={ShieldCheck}
            >
              <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
                <div className="flex items-center gap-3">
                  {mfaEnrolled
                    ? <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    : <ShieldOff  className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  }
                  <div>
                    <p className="text-sm font-medium">Authenticator App (TOTP)</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {mfaEnrolled === null ? 'Checking status…'
                        : mfaEnrolled ? 'Active — your account is protected'
                        : 'Not set up — required to access the app'}
                    </p>
                  </div>
                </div>
                <div>
                  {mfaEnrolled === false && (
                    <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate('/mfa/enroll')}>
                      <ShieldCheck className="h-3.5 w-3.5" /> Set Up
                    </Button>
                  )}
                  {mfaEnrolled === true && (
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => navigate('/mfa/enroll')}>
                      Re-enroll
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Use Google Authenticator or Authy. Re-enrolling will replace your existing authenticator code.
              </p>
            </Section>
          )}

          {/* Change Password — only for store owners */}
          {user?.role === 'owner' && (
            <form onSubmit={handleChangePassword}>
              <Section title="Change Password" desc="Update your login password" icon={Lock} badge="Required">
                <div className="grid grid-cols-1 gap-3">
                  <Field label="Current Password" id="old_pw">
                    <div className="relative">
                      <Input id="old_pw" type={showOldPw ? 'text' : 'password'} value={pwForm.old_password}
                        onChange={e => setPwForm(f => ({ ...f, old_password: e.target.value }))} placeholder="••••••••" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowOldPw(!showOldPw)}>
                        {showOldPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="New Password" id="new_pw">
                      <div className="relative">
                        <Input id="new_pw" type={showNewPw ? 'text' : 'password'} value={pwForm.new_password}
                          onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Min 6 chars" />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowNewPw(!showNewPw)}>
                          {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </Field>
                    <Field label="Confirm Password" id="conf_pw">
                      <Input id="conf_pw" type="password" value={pwForm.confirm}
                        onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Repeat password" />
                    </Field>
                  </div>
                </div>
                <div className="flex justify-end pt-1">
                  <Button type="submit" variant="outline" disabled={pwLoading} className="gap-2">
                    {pwLoading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Updating…</> : <><Lock className="h-4 w-4" /> Update Password</>}
                  </Button>
                </div>
              </Section>
            </form>
          )}

          {/* Distributor Data Access — only for store owners */}
          {user?.role === 'owner' && (
            <Section
              title="Distributor Data Access"
              desc="Control what your linked distributors can see about your store."
              icon={Truck}
            >
              {[
                {
                  key: 'sales_velocity',
                  label: 'Sales Velocity',
                  desc: 'Distributors can see how fast your products are selling and which items are top-selling.',
                },
                {
                  key: 'dead_stock',
                  label: 'Dead Stock Insights',
                  desc: 'Distributors can see which of their products have had no sales recently.',
                },
                {
                  key: 'expiry_tracking',
                  label: 'Expiry & Batch Data',
                  desc: 'Distributors can see batch expiry dates for their products in your store.',
                },
              ].map(({ key, label, desc }) => {
                const enabled = form.supplier_visibility?.[key] !== false;
                return (
                  <div key={key} className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      onClick={() => toggleVisibility(key)}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        enabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                );
              })}
              <div className="flex justify-end pt-1">
                <Button type="button" disabled={savingDistributor} onClick={handleSaveDistributorAccess} className="gap-2 min-w-[180px]">
                  {savingDistributor ? <><RefreshCw className="h-4 w-4 animate-spin" /> Saving…</>
                    : savedDistributor ? <><CheckCircle2 className="h-4 w-4" /> Saved!</>
                    : <><Save className="h-4 w-4" /> Save Access Settings</>}
                </Button>
              </div>
            </Section>
          )}

          {/* Team Logins — only for store owners */}
          {user?.role === 'owner' && (
            <Section title="Team Logins" desc="Manage username and password for your staff" icon={Users}>
              {subUsers.length === 0 ? (
                <p className="text-xs text-muted-foreground">No team logins yet. Contact support to add team members.</p>
              ) : (
                <div className="space-y-2">
                  {subUsers.map(su => {
                    const isEditing = editingSubUser?.id === su.id;
                    const roleBadge = su.role === 'manager'
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'bg-gray-100 text-gray-600 border-gray-200';
                    return (
                      <div key={su.id} className="rounded-lg border border-border p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{su.owner_name || su.username}</span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border capitalize ${roleBadge}`}>
                              {su.role}
                            </span>
                            {!su.is_active && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">Inactive</span>
                            )}
                          </div>
                          {!isEditing && (
                            <button
                              type="button"
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setEditingSubUser({ id: su.id, username: su.username, password: '' })}
                            >
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          )}
                        </div>

                        {!isEditing && (
                          <p className="text-xs text-muted-foreground">Username: <span className="font-mono text-foreground">{su.username}</span></p>
                        )}

                        {isEditing && (
                          <div className="space-y-2 pt-1">
                            <Field label="Username" id={`su-username-${su.id}`}>
                              <Input
                                id={`su-username-${su.id}`}
                                value={editingSubUser.username}
                                onChange={e => setEditingSubUser(prev => ({ ...prev, username: e.target.value }))}
                                placeholder="Login username"
                                className="h-8 text-xs"
                              />
                            </Field>
                            <Field label="New Password" id={`su-pw-${su.id}`}>
                              <Input
                                id={`su-pw-${su.id}`}
                                type="password"
                                value={editingSubUser.password}
                                onChange={e => setEditingSubUser(prev => ({ ...prev, password: e.target.value }))}
                                placeholder="Leave blank to keep current"
                                className="h-8 text-xs"
                              />
                            </Field>
                            <div className="flex gap-2 justify-end">
                              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                onClick={() => setEditingSubUser(null)}>
                                <XIcon className="h-3 w-3" /> Cancel
                              </Button>
                              <Button type="button" size="sm" className="h-7 text-xs gap-1"
                                disabled={subUserSaving} onClick={handleSubUserSave}>
                                {subUserSaving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Save
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground pt-1">To add or remove team members, contact support.</p>
                </div>
              )}
            </Section>
          )}
        </div>

        {/* RIGHT COLUMN — appearance + plan & account */}
        <div className="space-y-6">

          {/* Appearance */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="h-4 w-4 text-primary" />
                Appearance
              </CardTitle>
              <CardDescription>Choose your preferred colour scheme</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[
                  { value: 'light',  label: 'Light',  Icon: Sun     },
                  { value: 'system', label: 'System', Icon: Monitor },
                  { value: 'dark',   label: 'Dark',   Icon: Moon    },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 text-xs font-medium transition-colors
                      ${theme === value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan & Account</p>

          {/* Membership Card */}
          <Card className="overflow-hidden">
            <div className={`px-5 py-4 ${tier.popular ? 'bg-emerald-600' : 'bg-primary'} text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TierIcon className="h-5 w-5" />
                  <span className="font-bold text-lg">{tier.label}</span>
                  {tier.popular && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Most Popular</span>}
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">{tier.price}</div>
                  <div className="text-xs opacity-80">{tier.period}</div>
                </div>
              </div>
            </div>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-semibold capitalize px-2 py-0.5 rounded-full text-xs ${statusColor[computeEffectiveStatus(form.membership_status, form.membership_valid_until)] || statusColor.active}`}>
                    {computeEffectiveStatus(form.membership_status, form.membership_valid_until)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Member since</span>
                  <span className="font-medium">{fmtDate(form.membership_started_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valid until</span>
                  <span className={`font-medium ${expirySoon ? 'text-amber-600' : ''}`}>
                    {fmtDate(form.membership_valid_until)}
                    {days !== null && days >= 0 && <span className="text-xs text-muted-foreground ml-1">({days}d left)</span>}
                  </span>
                </div>
              </div>

              {/* Renew / contact CTA */}
              <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-2">
                <p className="text-xs text-muted-foreground">
                  {computeEffectiveStatus(form.membership_status, form.membership_valid_until) === 'expired'
                    ? 'Your plan has expired. Contact us to restore access.'
                    : expirySoon
                    ? `Your plan expires in ${days} day${days !== 1 ? 's' : ''}. Renew to avoid interruption.`
                    : 'To renew or change your plan, contact us on WhatsApp or call.'}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => openRenewChat(tier.label, 'renew')}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 transition-colors"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </button>
                  <a
                    href={SUPPORT_PHONE}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border hover:bg-muted text-xs font-medium py-2 transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call us
                  </a>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Plan includes</p>
                <ul className="space-y-1.5">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {normalisedTier !== 'enterprise' && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Upgrade your plan</p>
                    {TIER_ORDER.filter(k => TIER_ORDER.indexOf(k) > TIER_ORDER.indexOf(normalisedTier)).map((key) => {
                      const t = TIERS[key];
                      const Ico = t.icon;
                      return (
                        <button key={key}
                          onClick={() => openRenewChat(t.label, 'upgrade')}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm">
                          <div className="flex items-center gap-2">
                            <Ico className="h-4 w-4 text-primary" />
                            <span className="font-medium">{t.label}</span>
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <span>{t.price}{t.period}</span>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Business Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Business Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <StatCard
                  icon={PiggyBank}
                  label="Savings Identified"
                  value={stats ? fmtCurrency(stats.savings_identified) : '—'}
                  accent="bg-emerald-100 text-emerald-700"
                />
                <StatCard
                  icon={Package}
                  label="Products in Catalog"
                  value={stats?.storage_used_products ?? '—'}
                />
                <StatCard
                  icon={Zap}
                  label="Actions Pending"
                  value={stats?.smart_actions_pending ?? '—'}
                  accent="bg-orange-100 text-orange-600"
                />
                <StatCard
                  icon={CheckCircle2}
                  label="Actions Addressed"
                  value={stats?.smart_actions_addressed ?? '—'}
                  accent="bg-green-100 text-green-700"
                />
              </div>
              {stats?.last_import_date && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1 border-t border-border">
                  <Calendar className="h-3 w-3" />
                  Last import: {fmtDate(stats.last_import_date)}
                </div>
              )}
              {stats?.top_categories?.length > 0 && (
                <div className="pt-1 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1.5">Top categories</p>
                  <div className="flex flex-wrap gap-1">
                    {stats.top_categories.map((cat, i) => (
                      <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full text-foreground">{cat}</span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plan Comparison — collapsible */}
          <Card>
            <CardHeader className="pb-3">
              <button
                type="button"
                onClick={() => setShowPlanComparison(v => !v)}
                className="w-full flex items-center justify-between text-left"
              >
                <CardTitle className="text-sm">Compare Plans</CardTitle>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${showPlanComparison ? 'rotate-180' : ''}`} />
              </button>
            </CardHeader>
            {showPlanComparison && (
              <CardContent className="space-y-3 pt-0">
                {TIER_ORDER.map((key) => {
                  const t = TIERS[key];
                  const Ico = t.icon;
                  const isCurrent = key === normalisedTier;
                  return (
                    <div key={key} className={`rounded-lg border p-3 text-sm ${isCurrent ? 'bg-primary/5 border-primary/30' : 'bg-muted/20 border-border'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Ico className={`h-4 w-4 ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`font-semibold ${isCurrent ? 'text-primary' : ''}`}>{t.label}</span>
                          {t.popular && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Popular</span>}
                          {isCurrent && <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">Current</span>}
                        </div>
                        <span className="font-bold text-foreground">{t.price}<span className="text-xs font-normal text-muted-foreground">{t.period}</span></span>
                      </div>
                      <ul className="space-y-1">
                        {t.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                            <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>

          <HelpCard />
        </div>
      </div>
    </div>
  );
}
